import * as React from 'react';

import { observer } from 'mobx-react';

import {
    DEFAULT_DIGIT_OF_VERIFICATION_CODE,
    DEFAULT_STATUS,
    OtpStatus,
    SENT_STATUS
} from '~constants/Constants';
import useDebounce from '~utils/hooks/debounce';
import OTPInput, { AllowedInputTypes } from './OtpInput';

export interface IVerificationCodeProps {
    autoFocus?: boolean;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
    length?: number;
    onChange?(): void;
    onCompleted?(data: string): void;
    placeholder?: string;
    type?: AllowedInputTypes;
    status?: OtpStatus;
    value?: string;
    reset?: boolean;
    readonly?: boolean;
}

interface OTPCredential extends Credential {
    code: string;
}

interface OtpRequestOptions extends CredentialRequestOptions {
    otp?: {
        transport: string[];
    };
}

export const VerificationCode = observer((props: IVerificationCodeProps) => {
    const {
        autoFocus = false,
        inputProps,
        length = DEFAULT_DIGIT_OF_VERIFICATION_CODE,
        onChange,
        onCompleted,
        placeholder = '',
        type = 'number',
        status = DEFAULT_STATUS,
        value: defaultValue = '',
        reset = false,
        readonly
    } = props;

    const confirmCode = onCompleted && useDebounce<string>(onCompleted);

    const [otp, setOtp] = React.useState(defaultValue);

    const handleOTPChange = (otpCode: string) => {
        setOtp(otpCode);
        onChange?.();

        if (otpCode.length === length) {
            confirmCode?.(otpCode);
        }
    };

    React.useEffect(() => {
        reset && setOtp(defaultValue);
    }, [reset]);

    React.useEffect(() => {
        const fetchOtp = async () => {
            if ('OTPCredential' in window) {
                try {
                    const content = await navigator.credentials.get({
                        otp: { transport: ['sms'] }
                    } as OtpRequestOptions);
                    if (content && 'code' in content) {
                        const otpCode = (content as OTPCredential)?.code;
                        setOtp(otpCode);
                        confirmCode?.(otpCode);
                    }
                } catch (error) {
                    console.warn(`Error fetching OTP:'  ${error.message}`);
                }
            } else {
                console.warn('WebOTP API is not supported in this browser.');
            }
        };

        if (status === SENT_STATUS) {
            fetchOtp();
        }
    }, [status]);

    return (
        <OTPInput
            containerStyle="elc-iam-verification-code-container"
            inputStyle={`elc-iam-verification-code-item ${status}`}
            numInputs={length}
            onChange={handleOTPChange}
            placeholder={placeholder}
            renderSeparator={<span></span>}
            value={otp}
            inputType={type}
            renderInput={(otpProps) => <input {...otpProps} {...inputProps} readOnly={readonly} />}
            shouldAutoFocus={autoFocus}
        />
    );
});
